<template>
  <Transition name="banner">
    <div
      v-if="isImpersonating"
      class="impersonation-banner"
      role="alert"
      data-testid="impersonation-banner"
    >
      <div class="impersonation-banner__content">
        <span class="impersonation-banner__icon">&#128100;</span>
        <span class="impersonation-banner__message">
          {{ t('impersonation.viewingAs', { email: targetEmail }) }}
          <span v-if="remainingMinutes !== null" class="impersonation-banner__expiry">
            ({{ t('impersonation.expiresIn', { minutes: remainingMinutes }) }})
          </span>
        </span>
      </div>
      <div class="impersonation-banner__actions">
        <button
          type="button"
          class="impersonation-banner__end-button"
          :disabled="isEnding"
          @click="handleEndImpersonation"
        >
          <span v-if="isEnding" class="impersonation-banner__spinner"></span>
          {{ isEnding ? t('impersonation.ending') : t('impersonation.endSession') }}
        </button>
      </div>
      <p v-if="error" class="impersonation-banner__error">{{ t(`impersonation.${error}`) }}</p>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useImpersonation } from '@/composables/useImpersonation'

const { t } = useI18n()
const { isImpersonating, targetEmail, remainingMinutes, isEnding, error, endImpersonation } =
  useImpersonation()

function handleEndImpersonation() {
  endImpersonation()
}
</script>

<style scoped>
.impersonation-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-lg);
  /* Solid fill under `--color-text-inverse`, and the only thing telling a support agent
     they are acting as someone else — so it takes the fill white can sit on (#4239). */
  background: var(--color-semantic-warning-strong);
  color: var(--color-text-inverse);
  font-weight: var(--font-weight-medium);
}

.impersonation-banner__content {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.impersonation-banner__icon {
  font-size: var(--font-size-lg);
}

.impersonation-banner__message {
  font-size: var(--font-size-sm);
}

.impersonation-banner__expiry {
  opacity: 0.9;
  font-weight: var(--font-weight-normal);
}

.impersonation-banner__actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.impersonation-banner__end-button {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  background: var(--color-background-surface);
  border: none;
  /* Label on a white surface, so it takes the warning that reads as text (#4239). */
  color: var(--color-semantic-warning-text);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--radius-base);
  transition:
    background var(--transition-duration-fast) var(--transition-easing-ease-out),
    opacity var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.impersonation-banner__end-button:hover:not(:disabled) {
  background: var(--color-background-surface);
}

.impersonation-banner__end-button:disabled {
  cursor: not-allowed;
  background-color: var(--color-button-disabled);
  color: var(--color-button-disabled-text);
}

.impersonation-banner__spinner {
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.impersonation-banner__error {
  width: 100%;
  margin: 0;
  padding: var(--spacing-xs) 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-inverse);
  opacity: 0.9;
}

/* Transition animations */
.banner-enter-active,
.banner-leave-active {
  transition:
    opacity var(--transition-duration-normal) var(--transition-easing-ease-out),
    transform var(--transition-duration-normal) var(--transition-easing-ease-out);
}

.banner-enter-from,
.banner-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
